/**
 * Find brackets and quotation marks that are opened and never closed, or closed and
 * never opened, with the line and column of each.
 *
 * One stack handles everything: an opening mark is pushed, a closing mark pops its
 * partner. When a closing mark finds a different opener on top, the openers above its
 * partner are reported as unclosed (the likeliest slip is a forgotten ")" inside a
 * sentence); when it finds no partner at all it is reported as unopened.
 *
 * Marks that are not brackets are ignored on purpose: a straight apostrophe, a curly
 * apostrophe with no open single quote ("students’ essays", "rock ’n’ roll"), list
 * markers ("1)", "a)"), smileys (":)"), and inch or foot marks ("5'10\"").
 *
 * A straight double quote is the same character at both ends, so it is opened or closed
 * by what surrounds it: a space or bracket before it and a letter after it opens; a
 * letter or punctuation before it and a space after it closes; anything else toggles.
 */

export interface Pair {
  open: string;
  close: string;
  kind: "bracket" | "quote";
}

export const PAIRS: readonly Pair[] = [
  { open: "(", close: ")", kind: "bracket" },
  { open: "[", close: "]", kind: "bracket" },
  { open: "{", close: "}", kind: "bracket" },
  { open: "“", close: "”", kind: "quote" },
  { open: "‘", close: "’", kind: "quote" },
  { open: "«", close: "»", kind: "quote" },
  { open: "‹", close: "›", kind: "quote" },
  { open: "„", close: "“", kind: "quote" },
  { open: "（", close: "）", kind: "bracket" },
  { open: "［", close: "］", kind: "bracket" },
  { open: "【", close: "】", kind: "bracket" },
  { open: "「", close: "」", kind: "quote" },
  { open: "『", close: "』", kind: "quote" },
  { open: "《", close: "》", kind: "bracket" },
];

const STRAIGHT = '"';

export interface BalanceOptions {
  /** Check each paragraph on its own: the usual rule, except for dialogue that runs on. */
  perParagraph: boolean;
  /** Skip "1)", "a)" and "iv)" at the start of a line or after a space. */
  ignoreListMarkers: boolean;
  /** Skip ":)", ";-)" and ":-(". */
  ignoreSmileys: boolean;
  /** Pair ‘ with ’ (a ’ with no open ‘ is always taken as an apostrophe). */
  singleQuotes: boolean;
}

export const DEFAULT_BALANCE_OPTIONS: BalanceOptions = {
  perParagraph: true,
  ignoreListMarkers: true,
  ignoreSmileys: true,
  singleQuotes: true,
};

export type IssueKind = "unclosed" | "unopened";

export interface BalanceIssue {
  kind: IssueKind;
  /** The mark that has no partner. */
  char: string;
  /** What would fix it: the missing closer for an unclosed opener, the opener for a stray closer. */
  missing: string;
  start: number;
  end: number;
  /** 1-based. */
  line: number;
  /** 1-based, in characters. */
  column: number;
  /** The line's text around the mark. */
  context: string;
  /** Offset of `char` inside `context`. */
  contextOffset: number;
}

export interface PairTally {
  open: string;
  close: string;
  opens: number;
  closes: number;
}

export interface BalanceReport {
  issues: BalanceIssue[];
  /** Only pairs that occur in the text, plus straight quotes as one entry. */
  tallies: PairTally[];
  lines: number;
}

const RE_LINE_BREAK = /\r\n|\r|\n/g;
const RE_BLANK_LINE = /(?:\r\n|\r|\n)[ \t]*(?:\r\n|\r|\n)/g;
const RE_LIST_MARKER = /^\s*(?:\d{1,3}|[a-zA-Z]|[ivxIVX]{2,4})$/;
const RE_SMILEY_BEFORE = /[:;8][-^o]?$/;
const RE_LETTER = /[\p{L}\p{N}]/u;
const RE_SPACE_OR_EDGE = /^\s?$/;
const RE_OPENS_BEFORE = /^(?:\s|[([{—–“‘«-])?$/;
const RE_DIGIT = /\d/;

interface Entry {
  char: string;
  index: number;
  partner: string;
}

const OPENERS = new Map<string, string>();
const CLOSERS = new Map<string, string[]>();
for (const pair of PAIRS) {
  OPENERS.set(pair.open, pair.close);
  const openers = CLOSERS.get(pair.close) ?? [];
  openers.push(pair.open);
  CLOSERS.set(pair.close, openers);
}

function lineStarts(text: string): number[] {
  const starts = [0];
  for (const match of text.matchAll(RE_LINE_BREAK)) {
    starts.push(match.index + match[0].length);
  }
  return starts;
}

function lineOf(starts: readonly number[], offset: number): number {
  let low = 0;
  let high = starts.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (starts[middle] <= offset) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }
  return low;
}

const CONTEXT_RADIUS = 28;

function makeIssue(
  text: string,
  starts: readonly number[],
  entry: { char: string; index: number },
  kind: IssueKind,
  missing: string
): BalanceIssue {
  const line = lineOf(starts, entry.index);
  const lineStart = starts[line];
  const nextStart = starts[line + 1] ?? text.length + 1;
  const lineText = text
    .slice(lineStart, nextStart - 1)
    .replace(RE_TRAILING_CR, "");
  const column = entry.index - lineStart;
  const from = Math.max(0, column - CONTEXT_RADIUS);
  const to = Math.min(lineText.length, column + CONTEXT_RADIUS + 1);
  const prefix = from > 0 ? "…" : "";
  const suffix = to < lineText.length ? "…" : "";
  return {
    kind,
    char: entry.char,
    missing,
    start: entry.index,
    end: entry.index + entry.char.length,
    line: line + 1,
    column: column + 1,
    context: `${prefix}${lineText.slice(from, to)}${suffix}`,
    contextOffset: prefix.length + (column - from),
  };
}

/** Paragraph break offsets: blank lines. */
function paragraphEnds(text: string): number[] {
  return [...text.matchAll(RE_BLANK_LINE)].map((match) => match.index);
}

function isListMarker(text: string, index: number): boolean {
  const lineStart = text.lastIndexOf("\n", index - 1) + 1;
  return RE_LIST_MARKER.test(text.slice(lineStart, index));
}

function isSmiley(text: string, index: number): boolean {
  return RE_SMILEY_BEFORE.test(text.slice(Math.max(0, index - 2), index));
}

type Role = "open" | "close" | "toggle" | "ignore";

/** What a straight double quote is doing, judged by its neighbours. */
function straightRole(text: string, index: number): Role {
  const before = text[index - 1] ?? "";
  const after = text[index + 1] ?? "";
  if (RE_DIGIT.test(before) && !RE_LETTER.test(after)) {
    return "ignore";
  }
  const opens = RE_OPENS_BEFORE.test(before) && RE_LETTER.test(after);
  const closes =
    RE_LETTER.test(before) ||
    (!RE_SPACE_OR_EDGE.test(before) && RE_SPACE_OR_EDGE.test(after));
  if (opens && !closes) {
    return "open";
  }
  return closes && !opens ? "close" : "toggle";
}

const RE_TRAILING_CR = /\r$/;
const CURLY_SINGLE_OPEN = "\u2018";
const CURLY_SINGLE_CLOSE = "\u2019";

class Scanner {
  private stack: Entry[] = [];
  readonly issues: BalanceIssue[] = [];
  readonly opens = new Map<string, number>();
  readonly closes = new Map<string, number>();

  private readonly text: string;
  private readonly starts: readonly number[];
  private readonly options: BalanceOptions;

  constructor(
    text: string,
    starts: readonly number[],
    options: BalanceOptions
  ) {
    this.text = text;
    this.starts = starts;
    this.options = options;
  }

  private report(
    entry: { char: string; index: number },
    kind: IssueKind,
    missing: string
  ): void {
    this.issues.push(makeIssue(this.text, this.starts, entry, kind, missing));
  }

  private count(map: Map<string, number>, char: string): void {
    map.set(char, (map.get(char) ?? 0) + 1);
  }

  /** End of a paragraph: whatever is still open there is unclosed. */
  flush(): void {
    for (const entry of this.stack) {
      this.report(entry, "unclosed", entry.partner);
    }
    this.stack = [];
  }

  private closeMark(char: string, index: number): void {
    const partners = char === STRAIGHT ? [STRAIGHT] : (CLOSERS.get(char) ?? []);
    for (let depth = this.stack.length - 1; depth >= 0; depth -= 1) {
      if (partners.includes(this.stack[depth].char)) {
        for (const skipped of this.stack.slice(depth + 1)) {
          this.report(skipped, "unclosed", skipped.partner);
        }
        this.stack = this.stack.slice(0, depth);
        return;
      }
    }
    this.report({ char, index }, "unopened", partners[0] ?? "");
  }

  private straight(index: number): void {
    const role = straightRole(this.text, index);
    if (role === "ignore") {
      return;
    }
    this.count(this.opens, STRAIGHT);
    const open = this.stack.some((entry) => entry.char === STRAIGHT);
    if (role === "open" || (role === "toggle" && !open)) {
      this.stack.push({ char: STRAIGHT, index, partner: STRAIGHT });
    } else {
      this.closeMark(STRAIGHT, index);
    }
  }

  /** A ":-(" smiley opens nothing. */
  private skippedOpener(char: string, index: number): boolean {
    return (
      char === "(" &&
      this.options.ignoreSmileys &&
      isSmiley(this.text, index) &&
      !RE_LETTER.test(this.text[index + 1] ?? "")
    );
  }

  private skippedCloser(char: string, index: number): boolean {
    if (char !== ")") {
      return false;
    }
    return (
      (this.options.ignoreListMarkers && isListMarker(this.text, index)) ||
      (this.options.ignoreSmileys && isSmiley(this.text, index))
    );
  }

  private hasOpenPartner(char: string): boolean {
    const partners = CLOSERS.get(char) ?? [];
    return this.stack.some((entry) => partners.includes(entry.char));
  }

  mark(char: string, index: number): void {
    if (char === STRAIGHT) {
      this.straight(index);
      return;
    }
    const closer = OPENERS.get(char);
    const closes = CLOSERS.has(char);
    if (!(closer || closes)) {
      return;
    }
    const single = char === CURLY_SINGLE_OPEN || char === CURLY_SINGLE_CLOSE;
    if (single && !this.options.singleQuotes) {
      return;
    }
    const closesOpen = closes && this.hasOpenPartner(char);
    // A ’ with no open ‘ is an apostrophe; a ")" with no "(" may be a list marker.
    if (char === CURLY_SINGLE_CLOSE && !closesOpen) {
      return;
    }
    if (
      !closesOpen &&
      (this.skippedCloser(char, index) || this.skippedOpener(char, index))
    ) {
      return;
    }
    if (closesOpen || !closer) {
      this.count(this.closes, char);
      this.closeMark(char, index);
    } else {
      this.count(this.opens, char);
      this.stack.push({ char, index, partner: closer });
    }
  }
}

export function checkBalance(
  text: string,
  options: BalanceOptions = DEFAULT_BALANCE_OPTIONS
): BalanceReport {
  const starts = lineStarts(text);
  const breaks = options.perParagraph ? paragraphEnds(text) : [];
  const scanner = new Scanner(text, starts, options);
  let nextBreak = 0;
  for (let index = 0; index < text.length; index += 1) {
    while (nextBreak < breaks.length && breaks[nextBreak] <= index) {
      scanner.flush();
      nextBreak += 1;
    }
    scanner.mark(text[index], index);
  }
  scanner.flush();
  scanner.issues.sort((a, b) => a.start - b.start);
  return {
    issues: scanner.issues,
    tallies: tallies(scanner.opens, scanner.closes),
    lines: starts.length,
  };
}

function tallies(
  opens: ReadonlyMap<string, number>,
  closes: ReadonlyMap<string, number>
): PairTally[] {
  const rows: PairTally[] = [];
  for (const pair of PAIRS) {
    const opening = opens.get(pair.open) ?? 0;
    const closing = closes.get(pair.close) ?? 0;
    if (opening > 0 || closing > 0) {
      rows.push({
        open: pair.open,
        close: pair.close,
        opens: opening,
        closes: closing,
      });
    }
  }
  const straight = opens.get(STRAIGHT) ?? 0;
  if (straight > 0) {
    rows.push({ open: STRAIGHT, close: STRAIGHT, opens: straight, closes: 0 });
  }
  return rows;
}
